/**
 * Карточка консолидированного пакета руководству (М5, ТЗ §6.5). Рендерится в
 * карточке прогона `run.module === 'exec_pack'`. Показывает:
 *  — статус согласованности (принцип «одна цифра», Ц6): consistent / incomplete
 *    / defect (последний зарезервирован, backend пока не выставляет);
 *  — headline-KPI (первая метрика каждого утверждённого направления);
 *  — KPI по направлениям М1–М4 со ссылкой на прогон-источник (трассировка);
 *  — пояснительную записку (LLM, best-effort — может отсутствовать).
 *
 * Пакет собирается воркером из УЖЕ утверждённых прогонов М1–М4 и цифры не
 * пересчитывает. Пока сборка не завершена — `pack === null`; показываем
 * состояние «собирается» по статусу самого прогона.
 */
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useGetEconExecPackQuery } from '@shared/api';
import type { EconExecPackConsistency, EconKpiMetric, EconModuleKpi, EconRun } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { ECON_MODULES, isEconModuleId, isRunActive } from '@entities/economic/types';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Баннер согласованности: цвет по статусу Ц6. */
const Banner = styled.div<{ $tone: 'success' | 'warning' | 'danger' }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ $tone, theme }) => theme.colors.status[$tone]};
  background: ${({ $tone, theme }) => `${theme.colors.status[$tone]}14`};
`;

const BannerTitle = styled.div`
  font-weight: 650;
  font-size: 14.5px;
`;

const BannerHint = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SubLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const HeadlineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const HeadlineCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const HeadlineLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const HeadlineValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const ModuleBox = styled.div<{ $muted: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(4)};
  background: ${({ $muted, theme }) =>
    $muted ? theme.colors.backgroundElevated : theme.colors.surface};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const ModuleHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const ModuleTitle = styled.div`
  font-weight: 650;
  font-size: 14px;
`;

const MetricLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
`;

const MetricLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const MetricValue = styled.span`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const SourceLink = styled(Link)`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Narrative = styled.p`
  margin: 0;
  font-size: 13.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
`;

/** Единый формат KPI-значения по единице измерения. Рубли — целые с
 *  разделителями и знаком ₽; объёмы — до 2 знаков и «м³»; прочее — как есть. */
function fmtKpi(m: EconKpiMetric): string {
  if (m.unit === '₽') return `${Math.round(m.value).toLocaleString('ru-RU')} ₽`;
  if (m.unit === 'м³') return `${m.value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} м³`;
  return `${m.value.toLocaleString('ru-RU')}${m.unit ? ` ${m.unit}` : ''}`;
}

function moduleTitle(module: string): string {
  return isEconModuleId(module) ? ECON_MODULES[module].title : module;
}

interface BannerCopy {
  tone: 'success' | 'warning' | 'danger';
  title: string;
  hint: string;
}

function bannerFor(consistency: EconExecPackConsistency, finalized: number, total: number): BannerCopy {
  switch (consistency) {
    case 'consistent':
      return {
        tone: 'success',
        title: 'Цифры согласованы по всем обязательным направлениям',
        hint: 'Пакет готов к передаче руководству.',
      };
    case 'defect':
      return {
        tone: 'danger',
        title: 'Обнаружено расхождение цифр',
        hint: 'Публикация заблокирована — проверьте показатели направлений.',
      };
    case 'incomplete':
    default:
      return {
        tone: 'warning',
        title: `Пакет неполный: утверждено ${finalized} из ${total} направлений`,
        hint: 'Утвердите недостающие расчёты М1–М4 за период и пересоберите пакет.',
      };
  }
}

function ModuleCard({ m }: { m: EconModuleKpi }) {
  return (
    <ModuleBox $muted={!m.finalized}>
      <ModuleHead>
        <ModuleTitle>{moduleTitle(m.module)}</ModuleTitle>
        <Badge $tone={m.finalized ? 'success' : 'neutral'}>
          {m.finalized ? 'утверждён' : 'нет расчёта'}
        </Badge>
      </ModuleHead>
      {m.finalized ? (
        <>
          {m.metrics.map((metric) => (
            <MetricLine key={metric.label}>
              <MetricLabel>{metric.label}</MetricLabel>
              <MetricValue>{fmtKpi(metric)}</MetricValue>
            </MetricLine>
          ))}
          {m.source_run && (
            <SourceLink to={`/agents/econ/runs/${m.source_run}`}>
              Открыть расчёт-источник →
            </SourceLink>
          )}
        </>
      ) : (
        <Muted>Нет утверждённого расчёта за период — данные направления не вошли в пакет.</Muted>
      )}
    </ModuleBox>
  );
}

export function ExecPackCard({ run }: { run: EconRun }) {
  const { data, isLoading, isError } = useGetEconExecPackQuery(run.id);
  const pack = data?.pack ?? null;

  if (isLoading) return <Muted>Загрузка пакета…</Muted>;
  if (isError) return <Muted>Не удалось загрузить пакет. Обновите страницу.</Muted>;

  if (!pack) {
    return (
      <Muted>
        {isRunActive(run.status)
          ? 'Пакет собирается из утверждённых расчётов М1–М4 — подождите завершения обработки.'
          : 'Пакет ещё не собран за этот период. Запустите сборку в модуле «Пакет».'}
      </Muted>
    );
  }

  const modules = pack.kpi.modules;
  const finalized = modules.filter((m) => m.finalized).length;
  const banner = bannerFor(pack.consistency, finalized, modules.length);

  return (
    <Wrap>
      <Banner $tone={banner.tone} role="status">
        <BannerTitle>{banner.title}</BannerTitle>
        <BannerHint>{banner.hint}</BannerHint>
      </Banner>

      {pack.kpi.headline.length > 0 && (
        <div>
          <SubLabel>Ключевые показатели</SubLabel>
          <HeadlineGrid style={{ marginTop: 12 }}>
            {pack.kpi.headline.map((m) => (
              <HeadlineCard key={m.label}>
                <HeadlineLabel>{m.label}</HeadlineLabel>
                <HeadlineValue>{fmtKpi(m)}</HeadlineValue>
              </HeadlineCard>
            ))}
          </HeadlineGrid>
        </div>
      )}

      <div>
        <SubLabel>Показатели по направлениям</SubLabel>
        <ModuleGrid style={{ marginTop: 12 }}>
          {modules.map((m) => (
            <ModuleCard key={m.module} m={m} />
          ))}
        </ModuleGrid>
      </div>

      <div>
        <SubLabel>Пояснительная записка</SubLabel>
        {pack.narrative ? (
          <Narrative style={{ marginTop: 8 }}>{pack.narrative}</Narrative>
        ) : (
          <Muted style={{ marginTop: 8 }}>
            Пояснительная записка не сформирована — пакет собран, но текстовое
            пояснение недоступно.
          </Muted>
        )}
      </div>
    </Wrap>
  );
}
